import { Box, Paper, Stack, Typography, alpha } from "@mui/material";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import parse from "html-react-parser";

/*
  SummaryTab
  Renders the study pack's `summary` + `sections` + `highYieldPoints`
  + `practicalQuestions`. Mirrors the mobile Summary tab layout.
*/

function renderHtml(html) {
  if (!html) return null;
  return (
    <Box
      sx={{
        fontSize: 15,
        lineHeight: 1.7,
        "& p": { margin: 0, mb: 1.2 },
        "& p:last-of-type": { mb: 0 },
        "& ul, & ol": { margin: "8px 0 0", pl: 2.5 },
        "& li": { marginBottom: 0.5 },
        "& strong, & b": { fontWeight: 700 },
        "& em": { fontStyle: "italic" },
        "& h1, & h2, & h3, & h4": { mt: 1.5, mb: 0.8, fontWeight: 700 },
      }}
    >
      {parse(html || "")}
    </Box>
  );
}

export default function SummaryTab({ studyPack }) {
  if (!studyPack) return null;
  const summary = studyPack.summary || "";
  const sections = Array.isArray(studyPack.sections) ? studyPack.sections : [];
  const highYield = Array.isArray(studyPack.highYieldPoints) ? studyPack.highYieldPoints : [];
  const practical = Array.isArray(studyPack.practicalQuestions) ? studyPack.practicalQuestions : [];

  return (
    <Stack spacing={2.5}>
      {summary ? (
        <Paper
          variant="outlined"
          sx={(t) => ({
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            borderColor: t.palette.divider,
          })}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <MenuBookRoundedIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={700}>
              Summary
            </Typography>
          </Stack>
          {renderHtml(summary)}
        </Paper>
      ) : null}

      {sections.length ? (
        <Paper
          variant="outlined"
          sx={(t) => ({
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            borderColor: t.palette.divider,
          })}
        >
          <Typography variant="h6" fontWeight={700} mb={1.5}>
            Detailed sections
          </Typography>
          <Stack spacing={2}>
            {sections.map((s, i) => (
              <Box key={i}>
                <Typography fontWeight={700} sx={{ mb: 0.6 }}>
                  {s.heading || s.title || `Section ${i + 1}`}
                </Typography>
                {renderHtml(s.body || s.content || "")}
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {highYield.length ? (
        <Paper
          variant="outlined"
          sx={(t) => ({
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            borderColor: alpha(t.palette.warning.main, 0.4),
            backgroundColor: alpha(t.palette.warning.main, 0.05),
          })}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LightbulbRoundedIcon sx={{ color: "warning.main" }} fontSize="small" />
            <Typography variant="h6" fontWeight={700}>
              High-yield points
            </Typography>
          </Stack>
          <Stack component="ul" spacing={0.6} sx={{ pl: 2.5, m: 0 }}>
            {highYield.map((point, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ lineHeight: 1.6 }}>
                {point}
              </Typography>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {practical.length ? (
        <Paper
          variant="outlined"
          sx={(t) => ({
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            borderColor: t.palette.divider,
          })}
        >
          <Typography variant="h6" fontWeight={700} mb={1}>
            Practice questions
          </Typography>
          <Stack component="ol" spacing={0.6} sx={{ pl: 2.5, m: 0 }}>
            {practical.map((q, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ lineHeight: 1.6 }}>
                {q}
              </Typography>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}
